import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";

// Set axios base URL
const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
    ? ""
    : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    z-index: 1000;
    padding: 20px;
    padding-top: 100px;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 90vw;
    width: 600px;
    max-height: 80vh;
    overflow: hidden;
    position: relative;

    @media (max-width: 768px) {
        width: 95vw;
        max-height: 70vh;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.9);
    }
`;

const SearchContainer = styled.div`
    padding: 2rem;
    border-bottom: 1px solid #eee;

    @media (max-width: 768px) {
        padding: 1.5rem;
    }
`;

const SearchTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #333;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 1rem;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.3s ease;

    &:focus {
        border-color: #667eea;
    }

    @media (max-width: 768px) {
        padding: 0.8rem;
        font-size: 0.9rem;
    }
`;

const ResultsContainer = styled.div`
    max-height: 400px;
    overflow-y: auto;
    padding: 1rem;

    @media (max-width: 768px) {
        max-height: 300px;
        padding: 0.5rem;
    }
`;

const NoResults = styled.div`
    text-align: center;
    padding: 2rem;
    color: #666;
    font-size: 1rem;

    @media (max-width: 768px) {
        padding: 1rem;
        font-size: 0.9rem;
    }
`;

const SearchResult = styled.div`
    display: flex;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #f8f9fa;
    }

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 768px) {
        padding: 0.8rem;
    }
`;

const ResultThumbnail = styled.img`
    width: 80px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
    margin-right: 1rem;

    @media (max-width: 768px) {
        width: 60px;
        height: 45px;
        margin-right: 0.8rem;
    }
`;

const ResultInfo = styled.div`
    flex: 1;
`;

const ResultTitle = styled.h3`
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0.3rem;
    color: #333;

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`;

const ResultDescription = styled.p`
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 0.3rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: 768px) {
        font-size: 0.8rem;
        -webkit-line-clamp: 1;
    }
`;

const ResultCategory = styled.span`
    background: #f0f0f0;
    color: #666;
    padding: 0.2rem 0.5rem;
    border-radius: 3px;
    font-size: 0.75rem;

    @media (max-width: 768px) {
        font-size: 0.7rem;
        padding: 0.15rem 0.4rem;
    }
`;

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: string;
    category: string;
    youtube_url: string;
    thumbnail_url: string;
    custom_description: string;
}

interface SearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    videos: any[];
    onVideoSelect: (video: Content) => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, videos, onVideoSelect }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<Content[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setSearchResults([]);
            return;
        }

        const performSearch = async () => {
            try {
                setIsLoading(true);
                console.log("Searching for:", searchTerm);
                const response = await axios.get(`/api/search?q=${encodeURIComponent(searchTerm)}`);
                console.log("Search response:", response.data);
                if (response.data.success) {
                    setSearchResults(response.data.data || []);
                } else {
                    console.error("Search API returned error:", response.data);
                    setSearchResults([]);
                }
            } catch (error) {
                console.error("Search error:", error);
                setSearchResults([]);
            } finally {
                setIsLoading(false);
            }
        };

        const timeoutId = setTimeout(performSearch, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handleVideoClick = (video: Content) => {
        onVideoSelect(video);
        onClose();
        setSearchTerm("");
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
            setSearchTerm("");
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <CloseButton
                    onClick={() => {
                        onClose();
                        setSearchTerm("");
                    }}
                >
                    ✕
                </CloseButton>

                <SearchContainer>
                    <SearchTitle>동영상 검색</SearchTitle>
                    <SearchInput type="text" placeholder="제목, 설명, 카테고리로 검색하세요..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
                </SearchContainer>

                <ResultsContainer>
                    {searchTerm.trim() === "" ? (
                        <NoResults>검색어를 입력하세요</NoResults>
                    ) : isLoading ? (
                        <NoResults>검색 중...</NoResults>
                    ) : searchResults.length === 0 ? (
                        <NoResults>
                            검색 결과가 없습니다
                            {process.env.NODE_ENV === "development" && (
                                <div style={{fontSize: "0.8rem", marginTop: "0.5rem", color: "#999"}}>
                                    검색어: "{searchTerm}"
                                </div>
                            )}
                        </NoResults>
                    ) : (
                        searchResults.map((video) => (
                            <SearchResult key={video.id} onClick={() => handleVideoClick(video)}>
                                <ResultThumbnail src={video.thumbnail_url || "https://via.placeholder.com/120x67/ccc/666?text=No+Image"} alt={video.title} />
                                <ResultInfo>
                                    <ResultTitle>{video.title}</ResultTitle>
                                    <ResultDescription>{video.custom_description || video.description}</ResultDescription>
                                    <ResultCategory>{video.category}</ResultCategory>
                                </ResultInfo>
                            </SearchResult>
                        ))
                    )}
                </ResultsContainer>
            </ModalContent>
        </ModalOverlay>
    );
};

export default SearchModal;
